import { useCallback, useEffect, useState } from 'react';

/**
 * useDesktopNotifications
 * Manages browser Notification API permission and sending desktop notifications.
 * Permission state is persisted; actual permission is always read from the browser.
 *
 * Returns:
 *   permission: 'default' | 'granted' | 'denied'
 *   isEnabled: boolean (user opted in AND browser granted)
 *   requestPermission: async () => void
 *   showNotification: (title, options) => void
 *   toggleDesktopNotifications: () => void
 */
const useDesktopNotifications = () => {
  const [permission, setPermission] = useState(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });

  // User-level toggle (separate from browser permission)
  const [isEnabled, setIsEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem('desktopNotificationsEnabled');
      if (stored !== null) return stored === 'true';
      // Default: enabled if permission already granted
      return typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';
    } catch {
      return false;
    }
  });

  // Keep permission state in sync
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    setPermission(Notification.permission);
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'denied') return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        setIsEnabled(true);
        localStorage.setItem('desktopNotificationsEnabled', 'true');
      }
    } catch {
      // Some browsers use callback style
      Notification.requestPermission((result) => {
        setPermission(result);
        if (result === 'granted') {
          setIsEnabled(true);
          localStorage.setItem('desktopNotificationsEnabled', 'true');
        }
      });
    }
  }, []);

  const toggleDesktopNotifications = useCallback(async () => {
    if (!isEnabled) {
      // Turning on: request permission if needed
      if (permission !== 'granted') {
        await requestPermission();
      } else {
        setIsEnabled(true);
        localStorage.setItem('desktopNotificationsEnabled', 'true');
      }
    } else {
      setIsEnabled(false);
      localStorage.setItem('desktopNotificationsEnabled', 'false');
    }
  }, [isEnabled, permission, requestPermission]);

  /**
   * showNotification(title, options)
   * Only fires if permission is granted AND user has enabled desktop notifications
   * AND the document is not currently focused (avoid duplicate alerts)
   */
  const showNotification = useCallback((title, options = {}) => {
    if (!isEnabled) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    // Don't show if tab is active
    if (document.visibilityState === 'visible') return;
    try {
      const n = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        silent: true, // We handle sound ourselves
        ...options
      });
      // Auto-close after 6 seconds
      setTimeout(() => n.close(), 6000);
    } catch {
      // Silently fail
    }
  }, [isEnabled]);

  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  return {
    permission,
    isEnabled,
    isSupported,
    requestPermission,
    showNotification,
    toggleDesktopNotifications
  };
};

export default useDesktopNotifications;
